<template>
  <el-dialog title="아티스트 추가" :visible.sync="syncedVisible" :destroy-on-close="true" width="680px" class="artist-add-dialog">
    <div class="artist-add-body">
      <section class="artist-add-section">
        <div class="artist-add-section-header">
          <div>
            <h5 class="artist-add-section-title">그룹으로 추가</h5>
            <p class="artist-add-section-desc">{{ groupSectionDesc }}</p>
          </div>
          <base-button @click="toggleAllGroups" type="default" size="sm">전체 선택/해제</base-button>
        </div>
        <div class="artist-add-checkbox-box">
          <el-checkbox-group v-model="checkedGroupIds" class="artist-add-checkbox-grid">
            <el-checkbox v-for="group in artistGroups" :key="group.unitId" :label="group.unitId">{{ group.blipName || group.name }}</el-checkbox>
          </el-checkbox-group>
          <div v-if="!artistGroups.length" class="artist-add-empty">그룹 목록을 불러오는 중입니다…</div>
        </div>
      </section>

      <section v-if="showMembers" class="artist-add-section">
        <div class="artist-add-section-header">
          <div>
            <h5 class="artist-add-section-title">멤버로 추가</h5>
            <p class="artist-add-section-desc">그룹을 선택하면 소속 멤버를 선택할 수 있습니다.</p>
          </div>
          <base-button @click="toggleAllMembers" type="default" size="sm" :disabled="!memberOptions.length">전체 선택/해제</base-button>
        </div>
        <el-select v-model="memberGroupId" placeholder="그룹 선택" filterable clearable style="width: 100%" class="mb-2" @change="onMemberGroupChange">
          <el-option v-for="group in artistGroups" :key="group.unitId" :label="group.blipName || group.name" :value="group.unitId" />
        </el-select>
        <div class="artist-add-checkbox-box">
          <el-checkbox-group v-model="checkedMemberIds" class="artist-add-checkbox-grid">
            <el-checkbox v-for="member in memberOptions" :key="member.artistId" :label="member.artistId">{{ member.blipName || member.name }}</el-checkbox>
          </el-checkbox-group>
          <div v-if="!memberOptions.length" class="artist-add-empty">먼저 그룹을 선택해주세요.</div>
        </div>
      </section>
    </div>

    <span slot="footer">
      <base-button @click="close" type="default">취소</base-button>
      <base-button @click="apply" type="primary">적용</base-button>
    </span>
  </el-dialog>
</template>
<script>
import { BaseButton } from "@/components/index";
import { Toast } from "@/util/Toast";

export default {
  name: "ArtistGroupAddModal",
  components: {
    BaseButton,
  },
  props: {
    visible: {
      type: Boolean,
      required: true,
    },
    showMembers: {
      type: Boolean,
      default: true,
    },
    groupSectionDesc: {
      type: String,
      default: "체크한 그룹이 그대로 추가됩니다.",
    },
    preselectedGroupUnitIds: {
      type: Array,
      default: () => [],
    },
    resolvePreselectedMemberIds: {
      type: Function,
      default: null,
    },
    groupsQuery: {
      type: String,
      default: "isFilter=1",
    },
  },
  data() {
    return {
      artistGroups: [],
      checkedGroupIds: [],
      memberGroupId: null,
      memberOptions: [],
      checkedMemberIds: [],
    };
  },
  computed: {
    syncedVisible: {
      get() {
        return this.visible;
      },
      set(value) {
        this.$emit("update:visible", value);
      },
    },
  },
  watch: {
    visible(newVal) {
      if (newVal) this.onOpen();
    },
  },
  methods: {
    async onOpen() {
      this.checkedGroupIds = [...this.preselectedGroupUnitIds];
      this.memberGroupId = null;
      this.memberOptions = [];
      this.checkedMemberIds = [];
      if (this.artistGroups.length === 0) await this.loadArtistGroups();
    },
    async loadArtistGroups() {
      try {
        const res = await this.$axios.get(`/artistUnit?${this.groupsQuery}`);
        this.artistGroups = res.data.data || [];
      } catch (err) {
        Toast.error(err.response?.data?.error?.message ?? "그룹 목록 조회 중 에러가 발생하였습니다");
      }
    },
    async onMemberGroupChange(unitId) {
      this.checkedMemberIds = [];
      this.memberOptions = [];
      if (!unitId) return;
      try {
        const res = await this.$axios.get(`/artistUnit/${unitId}/members`);
        this.memberOptions = (res.data.data || []).filter((item) => item.isVisible === 1);
        if (this.resolvePreselectedMemberIds) {
          this.checkedMemberIds = this.resolvePreselectedMemberIds(unitId) || [];
        }
      } catch (err) {
        Toast.error(err.response?.data?.error?.message ?? "멤버 목록 조회 중 에러가 발생하였습니다");
      }
    },
    toggleAllGroups() {
      this.checkedGroupIds = this.checkedGroupIds.length === this.artistGroups.length ? [] : this.artistGroups.map((g) => g.unitId);
    },
    toggleAllMembers() {
      this.checkedMemberIds = this.checkedMemberIds.length === this.memberOptions.length ? [] : this.memberOptions.map((m) => m.artistId);
    },
    close() {
      this.$emit("update:visible", false);
    },
    apply() {
      const groupUnits = this.artistGroups.filter((group) => this.checkedGroupIds.includes(group.unitId));
      const memberUnits = this.showMembers ? this.memberOptions.filter((member) => this.checkedMemberIds.includes(member.artistId)) : [];
      this.$emit("apply", { groupUnits, memberUnits, memberGroupId: this.memberGroupId });
      this.close();
    },
  },
};
</script>
<style scoped>
.artist-add-body {
  text-align: left;
}
.artist-add-section + .artist-add-section {
  margin-top: 20px;
  padding-top: 20px;
  border-top: 1px solid #e9ecef;
}
.artist-add-section-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 10px;
}
.artist-add-section-title {
  margin: 0;
  font-size: 14px;
  font-weight: 700;
  color: #222a42;
}
.artist-add-section-desc {
  margin: 2px 0 0;
  font-size: 12px;
  color: #8492a6;
}
.artist-add-checkbox-box {
  max-height: 180px;
  overflow-y: auto;
  border: 1px solid #e9ecef;
  border-radius: 6px;
  background: #f8f9fa;
  padding: 10px 12px;
}
.artist-add-checkbox-grid {
  display: flex;
  flex-wrap: wrap;
}
.artist-add-checkbox-grid .el-checkbox {
  flex: 0 0 33.3333%;
  margin-left: 0;
  margin-right: 0;
  margin-bottom: 8px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.artist-add-empty {
  padding: 8px 0;
  color: #8492a6;
  font-size: 13px;
  text-align: center;
}
</style>
